import React from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { FileText, Truck, Receipt, FileCheck, RotateCcw } from 'lucide-react';
import type { DocumentType } from '../lib/invoiceUtils';

interface DocumentTypeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentTypeSelect: (type: DocumentType) => void;
}

const documentTypes: {
  type: DocumentType;
  label: string;
  description: string;
  icon: React.ReactNode;
  iconColor: string;
  bgColor: string;
}[] = [
  {
    type: 'invoice',
    label: 'Fattura',
    description: 'Fattura fiscale standard con numerazione progressiva',
    icon: <Receipt className="w-8 h-8" />,
    iconColor: 'text-primary-600',
    bgColor: 'bg-slate-50',
  },
  {
    type: 'invoice_with_delivery',
    label: 'Fattura accompagnatoria',
    description: 'Fattura che accompagna la merce durante il trasporto',
    icon: <Truck className="w-8 h-8" />,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  {
    type: 'proforma',
    label: 'Fattura pro forma',
    description: 'Documento preventivo senza valore fiscale',
    icon: <FileText className="w-8 h-8" />,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  {
    type: 'ddt',
    label: 'DDT - Documento di Trasporto',
    description: 'Documento che accompagna la merce durante il trasporto',
    icon: <Truck className="w-8 h-8" />,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
  },
  {
    type: 'credit_note',
    label: 'Nota di credito',
    description: 'Documento per stornare o rettificare una fattura esistente',
    icon: <RotateCcw className="w-8 h-8" />,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
  },
];

export const DocumentTypeSelectionModal: React.FC<DocumentTypeSelectionModalProps> = ({
  isOpen,
  onClose,
  onDocumentTypeSelect,
}) => {
  const handleSelect = (type: DocumentType) => {
    onDocumentTypeSelect(type);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-center">Seleziona tipo di documento</DialogTitle>
        </DialogHeader>

        <DialogBody className="grid grid-cols-1 gap-3 py-2 md:grid-cols-2 md:gap-4 md:py-4">
          {documentTypes.map((docType) => (
            <Button
              key={docType.type}
              variant="outline"
              className={`h-auto min-h-[104px] md:min-h-[160px] p-4 flex flex-col items-center justify-center text-center hover:shadow-lg transition-all duration-200 border-2 ${docType.bgColor}`}
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
              onClick={() => handleSelect(docType.type)}
            >
              <div className={`${docType.iconColor} mb-3 flex-shrink-0`}>
                {docType.icon}
              </div>
              <div className="w-full space-y-2">
                <div className="font-semibold text-gray-900 text-sm leading-tight break-words">
                  {docType.label}
                </div>
                <div className="text-xs text-gray-600 leading-tight break-words hyphens-auto">
                  {docType.description}
                </div>
              </div>
            </Button>
          ))}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
